import type {
    Dispatch,
    SetStateAction
} from "react";
import { useTypedSelector } from "hooks";
import useSessionEffect from "hooks/useSessionEffect";
import { SetupIdentifier } from "consts/setups";
import { getOptions } from "util/option";
import useCountries from "features/general/useCountries";
import useMerchantActions from "features/merchants/useMerchantActions";
import useSetupsActions from "features/setups/useActions";
import useUpdateOrCreateUser from "features/users/useUpdateOrCreateUser";
import {
    selectIsMerchantGroupsLoading,
    selectIsMerchantLoading,
    selectMerchantGroupsOptions
} from "features/merchants/selectors";
import { Merchant, MerchantResponse } from "features/merchants/types";
import { MerchantOption } from "features/merchants/helpers";
import useMerchantUserActions from "features/merchants/useMerchantUserActions";
import type { UserResponse } from "features/users/types";
import useActions from "features/general/useActions";
import { NotificationSeverity } from "features/general/types";
import { selectIsSetupsLoading } from "features/setups/selectors";
import { Create } from "ui/forms/Merchant";
import { FormField, useForm as useUserForm } from 'ui/forms/User';
import { useResourceContext } from "ui/widgets/Resource";
import { type MerchantFormState, MerchantStepForm } from "./types";

const merchantGroupsSessionEffectSymbol = Symbol('merchantGroupsSessionEffect');

export default function useMerchant(setActiveStep: Dispatch<SetStateAction<MerchantStepForm>>) {
    const { onClose } = useResourceContext();

    const { showNotication } = useActions();
    const {
        getAllMerchantGroups,
        postMerchant
    } = useMerchantActions();

    const {
        getSetupByIdentifier
    } = useSetupsActions();

    const { updateOrAddMerchantUserById } = useMerchantUserActions();

    const { countries, isCountriesLoading } = useCountries();

    const isMerchantRequestProcessing = useTypedSelector(selectIsMerchantLoading);
    const merchantGroupsOptions = useTypedSelector(selectMerchantGroupsOptions);
    const isMerchantGroupsLoading = useTypedSelector(selectIsMerchantGroupsLoading);
    const isSftpImportConsumersAllowedMerchantsFoldersLoading = useTypedSelector(selectIsSetupsLoading(SetupIdentifier.SftpImportConsumersAllowedMerchantsFolders));

    useSessionEffect(() => {
        getAllMerchantGroups();
        getSetupByIdentifier(SetupIdentifier.SftpImportConsumersAllowedMerchantsFolders);
    }, merchantGroupsSessionEffectSymbol);

    const user = useUpdateOrCreateUser({
        user: {},
        onUserUpdateOrCreate: ({ data }: UserResponse) => updateOrAddMerchantUserById(data)
    });
    const userForm = useUserForm(user);

    const createMerchant = async (merchant: Partial<Merchant>): Promise<MerchantResponse> => {
        const [request] = postMerchant(merchant);
        const response = await request as MerchantResponse
        showNotication({
            severity: NotificationSeverity.Success,
            message: response.message
        });
        return response;
    };

    const merchantCreateForm = Create.useForm({
        initialValues: {},
        isInitialValid: false,
        onSaveOrCreate: createMerchant,
    });

    return new Map<MerchantStepForm, MerchantFormState>([
        [
            MerchantStepForm.Merchant,
            {
                ...merchantCreateForm,
                onSaveOrCreate: createMerchant,
                bootstrapData: {
                    countries,
                    merchantGroupsOptions
                },
                isBootstrapDataLoading: (
                    isCountriesLoading ||
                    isMerchantGroupsLoading ||
                    isSftpImportConsumersAllowedMerchantsFoldersLoading
                ),
                renderTitle: () => 'Merchant',
                getActionPanelProps: () => ({
                    CancelActionProps: {
                        children: 'Save',
                        disabled: !merchantCreateForm.isValid,
                        loading: isMerchantRequestProcessing
                    },
                    cancelActionColor: 'primary' as 'primary',
                    SaveActionProps: {
                        children: 'Save and Create Merchant User',
                        disabled: !merchantCreateForm.isValid,
                        loading: isMerchantRequestProcessing
                    },
                    customActionStyle:{
                       variant:'outlined' as 'outlined',
                    },
                    onCancel: async () => {
                        await createMerchant(merchantCreateForm.values);

                        onClose();
                        merchantCreateForm.resetForm();
                    },
                    onSave: async () => {
                        const { data } = await createMerchant(merchantCreateForm.values);

                        setActiveStep(MerchantStepForm.MerchantUser);
                        userForm.setFieldValue(
                            FormField.Merchants,
                            getOptions(
                                [MerchantOption.fromMerchant(data), ...user.bootstrapData.merchants],
                                [data.coreId]
                            ),
                            true
                        );
                        merchantCreateForm.resetForm();
                    },

                }),
                id: String(MerchantStepForm.Merchant)
            }
        ],
        [
            MerchantStepForm.MerchantUser,
            {
                ...userForm,
                ...user,
                renderTitle: () => 'Merchant User',
                getActionPanelProps: () => ({
                    CancelActionProps: {
                        children: 'Create Merchant User',
                        disabled: !userForm.isValid,
                        loading: user.isCreateOrUpdaterequestLoading
                    },
                    SaveActionProps: {
                        children: 'Save and create new merchant user',
                        disabled: !userForm.isValid,
                        loading: user.isCreateOrUpdaterequestLoading
                    },

                    onCancel: async () => {
                        await userForm.submitForm();

                        onClose();
                        userForm.resetForm();
                        setActiveStep(MerchantStepForm.Merchant);
                    },
                    onSave: async () => {
                        await userForm.submitForm();
                        userForm.resetForm({
                            values: {
                                ...userForm.initialValues,
                                [FormField.Merchants]: userForm.values.merchants
                            }
                        });
                        setActiveStep(MerchantStepForm.MerchantUser);
                    }
                }),
                id: String(MerchantStepForm.MerchantUser)
            }
        ],
    ]);
};
